import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Building2, Users, CreditCard, BarChart3,
  HeadphonesIcon, FileText, Settings, Shield, Bell, Search,
  ChevronDown, LogOut, Menu, X, Sparkles, Globe, Activity,
  BookOpen, Scale, MessageSquare, User
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import NotificationBell from '../shared/NotificationBell';

const navigation = [
  { name: 'Dashboard', href: '/owner', icon: LayoutDashboard },
  { name: 'Facilities', href: '/owner/facilities', icon: Building2 },
  { name: 'Analytics', href: '/owner/analytics', icon: BarChart3 },
  { name: 'Billing', href: '/owner/billing', icon: CreditCard },
  { name: 'Users', href: '/owner/users', icon: Users },
  { name: 'Support', href: '/owner/support', icon: HeadphonesIcon },
  { name: 'Content', href: '/owner/content', icon: BookOpen },
  { name: 'Legal', href: '/owner/legal', icon: Scale },
  { name: 'Settings', href: '/owner/settings', icon: Settings },
];

const OwnerLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Get user from localStorage
    const storedUser = localStorage.getItem('nikosafe_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      // No user, redirect to home
      navigate('/');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('nikosafe_token');
    localStorage.removeItem('nikosafe_user');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const getUserInitial = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-[#0a1628] transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="font-bold text-white text-lg">NikoSafe</span>
            <span className="text-[var(--niko-teal)] text-xs block">OWNER PORTAL</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto lg:hidden text-white/60 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="px-4 py-6 space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-[var(--niko-teal)]/20 to-transparent text-white border-l-2 border-[var(--niko-teal)]'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon className={`w-5 h-5 ${isActive ? 'text-[var(--niko-teal)]' : ''}`} />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* System Status */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="bg-white/5 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-[var(--niko-green)]" />
              <span className="text-sm font-medium text-white">System Status</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[var(--niko-green)] rounded-full animate-pulse" />
              <span className="text-xs text-white/60">All systems operational</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Search */}
            <div className="hidden md:flex items-center flex-1 max-w-xl">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search facilities, users, tickets..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--niko-teal)] focus:border-transparent"
                />
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
              {/* Global indicator */}
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-[var(--niko-teal)]/10 rounded-full">
                <Globe className="w-4 h-4 text-[var(--niko-teal)]" />
                <span className="text-sm font-medium text-[var(--niko-teal)]">47 Facilities Live</span>
              </div>

              {/* Notifications */}
              <NotificationBell variant="light" />

              {/* Profile */}
              <div className="relative flex items-center gap-3 pl-4 border-l border-gray-200">
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-3 hover:bg-gray-50 rounded-lg p-1 transition-colors"
                  data-testid="profile-dropdown-button"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">{getUserInitial()}</span>
                  </div>
                  <div className="hidden lg:block text-left">
                    <p className="text-sm font-medium text-gray-900">{user?.name || 'User'}</p>
                    <p className="text-xs text-gray-500">{user?.role === 'OWNER' ? 'Platform Owner' : user?.role}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Profile Dropdown */}
                {profileOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setProfileOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                      <div className="p-4 border-b border-gray-100">
                        <p className="font-semibold text-gray-900">{user?.name}</p>
                        <p className="text-sm text-gray-500">{user?.email}</p>
                      </div>
                      <div className="p-2">
                        <Link
                          to="/profile"
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                          data-testid="profile-link"
                        >
                          <User className="w-4 h-4" />
                          My Profile
                        </Link>
                        <Link
                          to="/owner/settings"
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                          Settings
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors w-full"
                          data-testid="logout-dropdown-button"
                        >
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default OwnerLayout;
